import './App.css';
import {axiosInstance as axios} from './axios-config';
import AddCartScreen from './components/AddCartScreen';
import * as React from 'react';
import {useState, useEffect, useCallback} from 'react';
import CartSection from './components/CartSection';
import {message} from 'antd';
import Loader from './components/Loader';
import {
  sanitizePhoneNumber,
  resolveMessagingPhoneNumber,
} from './utils/phone';
const BEUrl = 'https://mcfsmik3g0.execute-api.us-east-1.amazonaws.com';
const CONTAINER_BASE_URL_CHATWOOT =
  'https://restapis.bitespeed.co/api/v1/chatwoot';
const CONTAINER_BASE_URL_SHOPPING =
  'https://restapis.bitespeed.co/api/v1/shops/shopping';
const CHATWOOT_HELPER_BASE_URL =
  'https://b3i4zxcefi.execute-api.us-east-1.amazonaws.com';

const MIN_PHONE_LENGTH = 6;

/**
 * Per-brand cap on the discount an agent can apply, configured on botConfig and
 * delivered by getSupportShoppingConfig. Falsy maxDiscountPercentage => no limit.
 *
 * A discount code's real value is only known once Shopify resolves it, so the
 * `static` type is capped on the backend rather than here.
 */
const getDiscountLimit = (cartItems, discountConfig, maxDiscountPercentage) => {
  const max = Number(maxDiscountPercentage || 0);
  const cartTotal = (cartItems || []).reduce(
    (acc, item) =>
      acc + (Number(item.price) || 0) * (Number(item.quantity) || 0),
    0
  );
  const maxDiscountAmount = max
    ? Math.round(((cartTotal * max) / 100) * 100) / 100
    : 0;
  const typedValue = Number(discountConfig?.discountValue) || 0;
  const effectiveDiscountAmount =
    discountConfig?.type === 'percentage'
      ? (cartTotal * typedValue) / 100
      : discountConfig?.type === 'flat'
      ? typedValue
      : 0;
  return {
    maxDiscountPercentage: max,
    maxDiscountAmount,
    isDiscountOverLimit:
      !!max &&
      cartTotal > 0 &&
      effectiveDiscountAmount > maxDiscountAmount + 0.01,
  };
};
const isValidPhoneNumber = phoneNumber =>
  Boolean(phoneNumber && phoneNumber.length >= MIN_PHONE_LENGTH);

// rest api url (prod) = https://restapis.bitespeed.co

function App() {
  const [searchValue, setSearchValue] = useState('');
  const [products, setProducts] = useState([]);
  const [shopDetailsConfig, setShopDetailsConfig] = useState({});
  const [detailsFromChatwoot, setDetailsFromChatwoot] = useState({
    phoneNumber: null,
    igsid: '16531369', // Use the igsid you provided
    accountId: 643,
    conversationId: null,
    userFirstName: '',
    agentId: null,
    shopUrl: 'nitishkastore.myshopify.com',
    contactId: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();
  const [apiCallLoading, setApiCallLoading] = useState(false);
  const [isMessageSent, setIsMessageSent] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [latestIncomingMessageTimestamp, setLatestIncomingMessageTimestamp] =
    useState(null);
  const [isTemplateAvailable, setIsTemplateAvailable] = useState(false);

  // Function to check if message is within 24 hours using backend logic
  const isMessageWithin24Hours = () => {
    if (!latestIncomingMessageTimestamp) {
      return false; // No message found
    }

    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
    const twentyFourHoursInSeconds = 24 * 60 * 60; // 24 hours in seconds
    const timeDifference = currentTime - latestIncomingMessageTimestamp;

    return timeDifference <= twentyFourHoursInSeconds;
  };

  // Function to determine if "Send in Chat" should be disabled based on channel
  const getSendInChatDisabled = () => {
    const isInstagram = detailsFromChatwoot.instagramSourceId;
    const isWhatsApp = getEffectivePhoneNumber();

    if (isInstagram) {
      // Instagram has no 24-hour window restriction
      return false;
    } else if (isWhatsApp) {
      // WhatsApp: check 24-hour window and template availability
      return !isMessageWithin24Hours() && !isTemplateAvailable;
    }

    // Unknown channel or no contact info
    return true;
  };

  // Function to check if WhatsApp template is available for the shop
  const checkTemplateAvailability = async () => {
    if (!shopDetailsConfig?.shopUrl) {
      setIsTemplateAvailable(false);
      return;
    }

    try {
      // Check if bot config exists and has productItemTemplateId
      const response = await axios.get(`https://restapis.bitespeed.co/api/v1/chatbot/botConfig`, {
        params: {
          shopUrl: shopDetailsConfig.shopUrl,
          shopPhoneNumber: shopDetailsConfig.shopPhoneNumber,
        },
      });

      const templateId =
        response?.data?.botConfig?.config?.productItemTemplateId ||
        response?.data?.botConfig?.productItemTemplateId ||
        response?.data?.config?.productItemTemplateId;
      if (templateId) {
        setIsTemplateAvailable(true);
      } else {
        setIsTemplateAvailable(false);
      }
    } catch (error) {
      console.error('Error checking template availability:', error);
      setIsTemplateAvailable(false);
    }
  };
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  // Product selection lives here rather than inside CollectionsScreen: opening the
  // cart swaps AddCartScreen out entirely, which used to unmount every checked
  // variant and every open panel along with it.
  const [selectedByCollection, setSelectedByCollection] = useState({});
  const [selectedAllProducts, setSelectedAllProducts] = useState([]);
  const [activePanelKey, setActivePanelKey] = useState(null);
  const [showAllProducts, setShowAllProducts] = useState(false);

  const handleSearchChange = e => {
    setSearchValue(e.target.value);
  };

  const [storeState, setStoreState] = useState({
    selected_screen: 'SELECT_PRODUCT',
    cart_items: [],
    shipping_details: {
      first_name:
        detailsFromChatwoot?.userFirstName ||
        shopDetailsConfig?.firstName ||
        '',
      last_name: detailsFromChatwoot?.userLastName ||
        shopDetailsConfig?.lastName || '',
      address1: shopDetailsConfig?.shippingConfig?.address1 || '',
      zip: shopDetailsConfig?.shippingConfig?.zip || '',
      city: shopDetailsConfig?.shippingConfig?.city || '',
      province: shopDetailsConfig?.shippingConfig?.province || '',
      country: shopDetailsConfig?.shippingConfig?.country || '',
      phone: shopDetailsConfig?.shippingConfig?.phone || '',
    },
    discountConfig: {
      type: 'percentage',
      discountValue: 0,
      value: 0,
    },
    shipping_charges: 0,
    tags: [],
    notes: '',
    allow_cod: true,
    allow_prepaid: true,
  });

  const getAccountId = () =>
    detailsFromChatwoot.accountId || shopDetailsConfig.accountId;

  const getInboxId = () =>
    detailsFromChatwoot.inboxId || shopDetailsConfig.inboxId;

  const getEffectivePhoneNumber = () => {
    const shippingPhone = sanitizePhoneNumber(
      storeState?.shipping_details?.phone
    );
    if (isValidPhoneNumber(shippingPhone)) {
      return shippingPhone;
    }

    const configPhone = sanitizePhoneNumber(shopDetailsConfig.phoneNumber);
    if (isValidPhoneNumber(configPhone)) {
      return configPhone;
    }
    return null;
  };

  // The conversation's WhatsApp identity as Chatwoot knows it -- always E.164
  // with a country code, and not editable by the agent. This is the yardstick
  // every outbound send is validated against.
  const getChatwootPhoneNumber = () =>
    sanitizePhoneNumber(
      shopDetailsConfig.phoneNumber || detailsFromChatwoot.phoneNumber
    );

  const getRawPhoneNumberForShipping = () =>
    storeState?.shipping_details?.phone ||
    shopDetailsConfig.phoneNumber ||
    detailsFromChatwoot.phoneNumber ||
    '';

  const getCartStorageBaseKey = () => {
    const accountId = getAccountId();
    if (!accountId) {
      return null;
    }
    const effectivePhoneNumber = getEffectivePhoneNumber();
    if (effectivePhoneNumber) {
      return `${effectivePhoneNumber}-${accountId}`;
    }
    if (detailsFromChatwoot.igsid) {
      return `IG-${detailsFromChatwoot.igsid}-${accountId}`;
    }
    if (detailsFromChatwoot.conversationId) {
      return `CONV-${detailsFromChatwoot.conversationId}-${accountId}`;
    }
    return null;
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  // Function to get cart data from localStorage as fallback
  const getCartFromLocalStorage = () => {
    console.log('DEBUG_CART: getCartFromLocalStorage called');

    try {
      const storeState = localStorage.getItem('consolidatedStoreState');
      if (!storeState) {
        console.log('DEBUG_CART: No data in localStorage');
        return null;
      }

      const parsedState = JSON.parse(storeState);
      const baseKey = getCartStorageBaseKey();
      if (!baseKey) {
        console.log('DEBUG_CART: Missing base key for localStorage lookup');
        return null;
      }
      console.log('DEBUG_CART: Looking for key in localStorage:', baseKey);

      // Find the most recent entry for this phone-account combination
      const matchingKeys = Object.keys(parsedState).filter(key =>
        key.startsWith(baseKey)
      );

      if (matchingKeys.length > 0) {
        // Get the most recent entry (highest timestamp)
        const latestKey = matchingKeys.sort().pop();
        console.log(
          'DEBUG_CART: Found cart data in localStorage',
          JSON.stringify(parsedState[latestKey].cart_items)
        );
        return parsedState[latestKey];
      }

      console.log('DEBUG_CART: No matching cart data in localStorage');
      return null;
    } catch (error) {
      console.error('DEBUG_CART: Error reading from localStorage:', error);
      return null;
    }
  };

  // Function to save cart data to localStorage as backup
  const saveCartToLocalStorage = cartData => {
    console.log(
      'DEBUG_CART: saveCartToLocalStorage called with',
      JSON.stringify(cartData.cart_items)
    );

    try {
      const existingData = localStorage.getItem('consolidatedStoreState');
      const parsedData = existingData ? JSON.parse(existingData) : {};

      const baseKey = getCartStorageBaseKey();
      if (!baseKey) {
        console.log(
          'DEBUG_CART: Missing base key, skipping saveCartToLocalStorage'
        );
        return;
      }

      // Add timestamp to prevent conflicts between multiple iframe instances
      const timestamp = Date.now();
      const phoneNumberAccountId = `${baseKey}-${timestamp}`;
      console.log(
        'DEBUG_CART: Using key for localStorage:',
        phoneNumberAccountId
      );

      const updatedData = {
        ...parsedData,
        [phoneNumberAccountId]: cartData,
      };

      localStorage.setItem(
        'consolidatedStoreState',
        JSON.stringify(updatedData)
      );
      console.log('DEBUG_CART: Successfully saved to localStorage');
    } catch (error) {
      console.error('DEBUG_CART: Error saving to localStorage:', error);
    }
  };

  // Add a function to save cart products to localStorage
  const saveCartProductsToLocalStorage = products => {
    try {
      // Get existing cached products or initialize empty object
      const existingCache = localStorage.getItem('cartProductsCache') || '{}';
      const parsedCache = JSON.parse(existingCache);

      // Create a unique key based on shop URL
      const cacheKey = shopDetailsConfig.shopUrl || 'default';

      // Update the cache with new products
      const updatedCache = {
        ...parsedCache,
        [cacheKey]: {
          products: products,
          timestamp: Date.now(), // Add timestamp for potential expiry check later
        },
      };

      localStorage.setItem('cartProductsCache', JSON.stringify(updatedCache));
      console.log('DEBUG_CART: Saved cart products to localStorage cache');
    } catch (error) {
      console.error('DEBUG_CART: Error saving cart products to cache:', error);
    }
  };

  const updateContactPhoneNumberInChatwoot = async phoneNumber => {
    const contactId = detailsFromChatwoot.contactId;
    const accountId = getAccountId();
    if (!contactId || !accountId) {
      return;
    }

    await axios.post(`${CHATWOOT_HELPER_BASE_URL}/chatwoot/updatePhoneNumber`, {
      accountId,
      contactId,
      phoneNumber,
    });
  };

  // Add a function to get cart products from localStorage
  const getCartProductFromLocalStorage = productId => {
    try {
      const cache = localStorage.getItem('cartProductsCache');
      if (!cache) return null;

      const parsedCache = JSON.parse(cache);
      const cacheKey = shopDetailsConfig.shopUrl || 'default';

      if (!parsedCache[cacheKey] || !parsedCache[cacheKey].products)
        return null;

      // Find the product in the cached products
      const cachedProduct = parsedCache[cacheKey].products.find(
        p => p.shopifyId === String(productId)
      );

      console.log(
        'DEBUG_CART: parsedCache[cacheKey]',
        parsedCache[cacheKey].products
      );

      if (cachedProduct) {
        console.log('DEBUG_CART: Found product in cache for', productId);
        return cachedProduct;
      }

      console.log('DEBUG_CART: Product not found in cache for', productId);
      return null;
    } catch (error) {
      console.error('DEBUG_CART: Error retrieving product from cache:', error);
      return null;
    }
  };

  // Modify the updateCartOnServer function to use and update the cache
  const updateCartOnServer = async (
    cartItems,
    discountConfig = storeState.discountConfig,
    overrideEffectivePhoneNumber
  ) => {
    console.log('DEBUG_CART: updateCartOnServer called with', {
      cartItems: JSON.stringify(cartItems),
      discountConfig,
    });

    try {
      const formattedPhoneNumber =
        overrideEffectivePhoneNumber || getEffectivePhoneNumber();
      if (!shopDetailsConfig.shopUrl || !formattedPhoneNumber) {
        console.log(
          'DEBUG_CART: Missing shopUrl or phoneNumber, aborting server update'
        );
        return false;
      }

      // Array to collect products that need to be cached
      const productsToCache = [];

      // Format cart items for the API. The cart item already carries everything the
      // server cart needs, so the product lookup only enriches it (image, full
      // variant). A failed lookup must never drop the item: the server cart would
      // be saved empty while the screen still shows it (CUS-30819).
      console.log('DEBUG_CART: Formatting cart items for API');
      const formattedCartItems = await Promise.all(
        cartItems.map(async item => {
          if (item.variant_id === undefined || item.variant_id === null) {
            console.error('DEBUG_CART: Cart item has no variant_id', item);
            return null;
          }

          // First check in the current products state
          let product = products.find(
            p => String(p.productId) === String(item.product_id)
          );

          // If not found in current state, check in localStorage cache
          if (!product) {
            product = getCartProductFromLocalStorage(item.product_id);
          }

          // If still not found, fetch from API
          if (!product && item.product_id) {
            console.log('DEBUG_CART: Product not found for', item.product_id);

            try {
              const response = await axios.get(
                `${CONTAINER_BASE_URL_CHATWOOT}/getProducts/${shopDetailsConfig.shopUrl}?productId=${item.product_id}`
              );
              product = response.data;

              if (product) {
                console.log('DEBUG_CART: Product found for', item.product_id);
                productsToCache.push(product);
              }
            } catch (error) {
              console.error('DEBUG_CART: Error fetching product', error);
            }
          }

          const lookedUpVariant = Array.isArray(product?.variants)
            ? product.variants.find(
                v => String(v.id) === String(item.variant_id)
              )
            : null;
          if (!lookedUpVariant) {
            console.log(
              'DEBUG_CART: Variant not found, using cart item data for',
              item.variant_id
            );
          }

          const variant = lookedUpVariant || {
            id: item.variant_id,
            title: item.variantTitle,
            price: item.price,
            sku: item.sku,
          };
          const image =
            typeof product?.image === 'string'
              ? {src: product.image}
              : product?.image || null;

          return {
            productDetails: {
              id: item.product_id,
              title: product?.title || item.title,
              variants: [variant],
              image,
            },
            variant: variant,
            quantity: item.quantity,
            price: variant.price ?? item.price,
            // Rides along in cartData, which the connector stores as a JSON blob
            // verbatim and hands back unchanged from getCart.
            collectionId: item.collection_id || null,
          };
        })
      );

      // If we fetched any new products, cache them
      if (productsToCache.length > 0) {
        saveCartProductsToLocalStorage(productsToCache);
      }

      // Filter out any null items
      const validCartItems = formattedCartItems.filter(item => item !== null);
      console.log(
        'DEBUG_CART: Valid formatted cart items',
        JSON.stringify(validCartItems)
      );

      // Never save a cart that differs from the one on screen: the checkout link
      // is built from the server cart, so a partial save would silently drop items.
      if (validCartItems.length !== cartItems.length) {
        console.error('DEBUG_CART: Some cart items could not be synced', {
          cartItems,
          validCartItems,
        });
        messageApi.error(
          'Some cart items could not be saved. Please remove and re-add them.'
        );
        return false;
      }

      // Make API call to update cart
      console.log('DEBUG_CART: Making API call to update cart');
      await axios.post(
        `${CONTAINER_BASE_URL_CHATWOOT}/modifyCart/${shopDetailsConfig.shopUrl}/${formattedPhoneNumber}`,
        {
          cartData: {
            cartItems: validCartItems,
            discount: {
              type: discountConfig.type,
              value_type: discountConfig.type === 'percentage' ? 'percentage' : discountConfig.type === 'flat' ? 'fixed_amount' : discountConfig.type,
              value: discountConfig.type === 'percentage' ? Number(discountConfig.discountValue) || 0 : discountConfig.value,
              amount: discountConfig.value,
              discountValue: discountConfig.discountValue,
            },
            shippingCharges: storeState.shipping_charges,
          },
        }
      );

      // Save to localStorage with the updated cart items
      const localStorageData = {
        ...storeState,
        cart_items: cartItems,
        discountConfig: discountConfig,
      };
      console.log(
        'DEBUG_CART: Saving to localStorage',
        JSON.stringify(localStorageData.cart_items)
      );
      saveCartToLocalStorage(localStorageData);

      console.log('DEBUG_CART: Cart updated successfully on server');
      return true;
    } catch (error) {
      console.error('DEBUG_CART: Error updating cart on server', error);
      messageApi.error('Error updating cart on server');

      // Even if server update fails, save to localStorage
      const fallbackData = {
        ...storeState,
        cart_items: cartItems,
      };
      console.log(
        'DEBUG_CART: Saving to localStorage after error',
        JSON.stringify(fallbackData.cart_items)
      );
      saveCartToLocalStorage(fallbackData);
      return false;
    }
  };

  const fetchCartData = async (shopUrl, phoneNumber) => {
    console.log('DEBUG_CART: fetchCartData called with', {
      shopUrl,
      phoneNumber,
    });

    try {
      const formattedPhoneNumber = sanitizePhoneNumber(phoneNumber);
      if (!shopUrl || !formattedPhoneNumber) {
        console.log(
          'DEBUG_CART: Missing shopUrl or phoneNumber, aborting fetch'
        );
        return null;
      }

      console.log('DEBUG_CART: Fetching cart data from API');
      const response = await axios.get(
        `${CONTAINER_BASE_URL_CHATWOOT}/getCart/${shopUrl}/${formattedPhoneNumber}`
      );

      const cartData = response.data;
      console.log('DEBUG_CART: Fetched cart data:', JSON.stringify(cartData));

      if (
        cartData.cartResult &&
        cartData.cartResult.success &&
        cartData.cartResult.cartData
      ) {
        // Extract cart items from the API response
        const cartItems = Array.isArray(cartData.cartResult.cartData.cartItems)
          ? cartData.cartResult.cartData.cartItems.map(item => ({
              variant_id: item.variant.id,
              title: item.productDetails.title,
              price: item.variant.price,
              variantTitle: item.variant.title,
              sku: item.variant.sku,
              quantity: item.quantity,
              product_id: item.productDetails.id,
              // Absent for carts built before provenance was recorded; the
              // section badge falls back to matching loaded products then.
              collection_id: item.collectionId || null,
            }))
          : [];

        console.log(
          'DEBUG_CART: Extracted cart items from API',
          JSON.stringify(cartItems)
        );

        // If cart is empty, try to get data from localStorage
        if (cartItems.length === 0) {
          console.log('DEBUG_CART: Cart is empty, trying localStorage');
          const localCartData = getCartFromLocalStorage();
          if (
            localCartData &&
            localCartData.cart_items &&
            localCartData.cart_items.length > 0
          ) {
            console.log(
              'DEBUG_CART: Using cart from localStorage',
              JSON.stringify(localCartData.cart_items)
            );
            // Recalculate discount value based on cart total
            if (localCartData.discountConfig && localCartData.cart_items) {
              const localTotalCartValue = localCartData.cart_items.reduce(
                (acc, item) =>
                  acc + (Number(item.price) || 0) * (Number(item.quantity) || 0),
                0
              );
              const discType = localCartData.discountConfig.type;
              const discVal = localCartData.discountConfig.discountValue ?? 0;
              if (discType === 'percentage') {
                localCartData.discountConfig.value = (localTotalCartValue * parseFloat(discVal || 0)) / 100;
              } else if (discType === 'flat') {
                localCartData.discountConfig.value = parseFloat(discVal || 0);
              }
            }
            return localCartData;
          }
        }

        // Extract customer details if available
        const customerDetails =
          cartData.customerDetailsResult &&
          cartData.customerDetailsResult.success &&
          cartData.customerDetailsResult.customerDetails;

        // Extract shipping address from customer details if available
        const shippingAddress =
          customerDetails?.defaultAddress || storeState.shipping_details;

        // Calculate total cart value for discount calculation
        const totalCartValue = cartItems.reduce(
          (acc, item) =>
            acc + (Number(item.price) || 0) * (Number(item.quantity) || 0),
          0
        );

        // Get discount config from API
        const discountType = cartData.cartResult.cartData.discount?.type || 'percentage';
        const discountValue = cartData.cartResult.cartData.discount?.discountValue ?? cartData.cartResult.cartData.discount?.value ?? 0;

        // Recalculate the actual discount value based on type and cart total
        let calculatedValue = 0;
        if (discountType === 'percentage') {
          calculatedValue = (totalCartValue * parseFloat(discountValue || 0)) / 100;
        } else if (discountType === 'flat') {
          calculatedValue = parseFloat(discountValue || 0);
        } else {
          calculatedValue = discountValue || 0;
        }

        // Create updated state with all available data
        const updatedCartData = {
          selected_screen: storeState.selected_screen,
          cart_items: cartItems,
          shipping_details: {
            first_name:
              shippingAddress?.first_name ||
              storeState.shipping_details.first_name,
            last_name:
              shippingAddress?.last_name ||
              storeState.shipping_details.last_name,
            address1:
              shippingAddress?.address1 || storeState.shipping_details.address1,
            zip: shippingAddress?.zip || storeState.shipping_details.zip,
            city: shippingAddress?.city || storeState.shipping_details.city,
            province:
              shippingAddress?.province || storeState.shipping_details.province,
            country:
              shippingAddress?.country || storeState.shipping_details.country,
            phone:
              storeState.shipping_details.phone ||
              shippingAddress?.phone ||
              phoneNumber,
          },
          discountConfig: {
            type: discountType,
            discountValue: discountValue,
            value: calculatedValue,
          },
          shipping_charges: cartData.cartResult.cartData.shippingCharges || 0,
          tags: storeState.tags,
          notes: storeState.notes,
          allow_cod: storeState.allow_cod,
          allow_prepaid: storeState.allow_prepaid,
        };

        console.log(
          'DEBUG_CART: Created updated cart data',
          JSON.stringify(updatedCartData.cart_items)
        );

        // Save to localStorage as backup
        saveCartToLocalStorage(updatedCartData);

        return updatedCartData;
      }

      // If no cart data is found, try to get data from localStorage
      console.log('DEBUG_CART: No cart data from API, trying localStorage');
      const localCartData = getCartFromLocalStorage();
      if (localCartData) {
        console.log(
          'DEBUG_CART: Using cart from localStorage',
          JSON.stringify(localCartData.cart_items)
        );
        // Recalculate discount value based on cart total
        if (localCartData.discountConfig && localCartData.cart_items) {
          const localTotalCartValue = localCartData.cart_items.reduce(
            (acc, item) =>
              acc + (Number(item.price) || 0) * (Number(item.quantity) || 0),
            0
          );
          const discType = localCartData.discountConfig.type;
          const discVal = localCartData.discountConfig.discountValue ?? 0;
          if (discType === 'percentage') {
            localCartData.discountConfig.value = (localTotalCartValue * parseFloat(discVal || 0)) / 100;
          } else if (discType === 'flat') {
            localCartData.discountConfig.value = parseFloat(discVal || 0);
          }
        }
        return localCartData;
      }

      console.log('DEBUG_CART: No cart data found anywhere');
      return null;
    } catch (error) {
      console.error('DEBUG_CART: Error fetching cart data:', error);
      messageApi.error('Error fetching cart data');

      // On error, try to get data from localStorage
      console.log('DEBUG_CART: Error occurred, trying localStorage');
      const localCartData = getCartFromLocalStorage();
      if (localCartData) {
        console.log(
          'DEBUG_CART: Using cart from localStorage after error',
          JSON.stringify(localCartData.cart_items)
        );
        // Recalculate discount value based on cart total
        if (localCartData.discountConfig && localCartData.cart_items) {
          const localTotalCartValue = localCartData.cart_items.reduce(
            (acc, item) =>
              acc + (Number(item.price) || 0) * (Number(item.quantity) || 0),
            0
          );
          const discType = localCartData.discountConfig.type;
          const discVal = localCartData.discountConfig.discountValue ?? 0;
          if (discType === 'percentage') {
            localCartData.discountConfig.value = (localTotalCartValue * parseFloat(discVal || 0)) / 100;
          } else if (discType === 'flat') {
            localCartData.discountConfig.value = parseFloat(discVal || 0);
          }
        }
        return localCartData;
      }

      return null;
    }
  };

  // Add a ref to track the latest update timestamp
  const latestUpdateTimestampRef = React.useRef(0);

  // Modify the debouncedUpdateCartOnServer function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdateCartOnServer = useCallback(
    debounce(
      (cartItems, discountConfig = storeState.discountConfig, timestamp) => {
        // Only process this update if it's the latest one
        if (timestamp !== latestUpdateTimestampRef.current) {
          console.log(
            'DEBUG_CART: Skipping outdated update',
            timestamp,
            'latest is',
            latestUpdateTimestampRef.current
          );
          return;
        }

        console.log('DEBUG_CART: Processing latest update', timestamp);

        // Use the passed discount config or fall back to the state
        const currentDiscountConfig =
          discountConfig || storeState.discountConfig;

        // Call updateCartOnServer with the explicit discount config
        updateCartOnServer(cartItems, currentDiscountConfig);
      },
      1000
    ),
    [shopDetailsConfig, products, storeState]
  );

  // Function to validate if a product has proper variants and can be added to cart
  const isProductValid = product => {
    try {
      // Check if product exists
      if (!product) {
        console.warn('Product validation: Product is null/undefined');
        return false;
      }

      // Check if product has variants array
      if (!product.variants || !Array.isArray(product.variants)) {
        console.warn('Product validation: No variants array', {
          productId: product.productId,
          title: product.title,
        });
        return false;
      }

      // Check if variants array is not empty
      if (product.variants.length === 0) {
        console.warn('Product validation: Empty variants array', {
          productId: product.productId,
          title: product.title,
        });
        return false;
      }

      // Check if all variants have required properties
      const hasInvalidVariants = product.variants.some(variant => {
        return (
          !variant ||
          typeof variant.id === 'undefined' ||
          variant.id === null ||
          !variant.title ||
          typeof variant.price === 'undefined'
        );
      });

      if (hasInvalidVariants) {
        console.warn('Product validation: Invalid variants found', {
          productId: product.productId,
          title: product.title,
          variants: product.variants,
        });
        return false;
      }

      return true;
    } catch (error) {
      console.error('Product validation error:', error, {product});
      return false;
    }
  };

  const updateCartItems = (variantId, productId, quantity, productContext) => {
    console.log('DEBUG_CART: updateCartItems called with', {
      variantId,
      productId,
      quantity,
    });
    // if the item doesn't exist for product add it.
    const productExists = storeState.cart_items.find(
      item => item.variant_id === variantId
    );

    console.log(
      'DEBUG_CART: Current cart items',
      JSON.stringify(storeState.cart_items)
    );
    console.log('DEBUG_CART: Product exists?', productExists);
    console.log(
      'DEBUG_CART: Available filtered products count:',
      filteredProducts.length
    );
    console.log(
      'DEBUG_CART: Available filtered products:',
      filteredProducts.map(p => ({
        productId: p.productId,
        id: p.id,
        title: p.title,
        hasVariants: !!p.variants,
      }))
    );
    console.log('DEBUG_CART: Looking for productId:', productId);

    let updatedCartItems;

    if (!productExists) {
      // Try to find product in filteredProducts, then in full products list
      let product = filteredProducts.find(
        p => String(p.productId) === String(productId)
      );
      if (!product) {
        product = products.find(p => String(p.productId) === String(productId));
      }

      // Fallback to localStorage cache if not found in memory
      if (!product) {
        console.error('DEBUG_CART: Product not found', {
          productId,
          filteredProducts,
        });
        const cachedProduct = getCartProductFromLocalStorage(productId);
        if (cachedProduct) {
          product = cachedProduct;
        }
      }

      // If product is still not available but we have context, use it to create the cart item
      if (!product && productContext) {
        updatedCartItems = [
          ...storeState.cart_items,
          {
            variant_id: variantId,
            quantity: quantity,
            product_id: productId,
            title: productContext.productTitle,
            price: productContext.price,
            variantTitle: productContext.variantTitle,
            sku: productContext.sku,
          },
        ];
        console.log('DEBUG_CART: Adding new item to cart using context', {
          variant_id: variantId,
          quantity,
          product_id: productId,
        });
      } else {
        if (!product || !product.variants || !Array.isArray(product.variants)) {
          console.error('DEBUG_CART: Product variants not found or invalid', {
            product,
          });
          return; // Exit early if variants are not available and no context
        }

        const variant = product.variants.find(
          v => String(v.id) === String(variantId)
        );

        if (!variant && !productContext) {
          console.error('DEBUG_CART: Variant not found', {
            variantId,
            variants: product.variants,
          });
          return; // Exit early if variant is not found and no context
        }

        updatedCartItems = [
          ...storeState.cart_items,
          {
            variant_id: variantId,
            quantity: quantity,
            product_id: productId,
            title: product?.title || productContext?.productTitle,
            price: (variant && variant.price) || productContext?.price,
            variantTitle:
              (variant && variant.title) || productContext?.variantTitle,
            sku: (variant && variant.sku) || productContext?.sku,
          },
        ];
        console.log('DEBUG_CART: Adding new item to cart', {
          variant_id: variantId,
          quantity,
          product_id: productId,
        });
      }
    } else if (quantity === 0) {
      updatedCartItems = storeState.cart_items.filter(
        item => item.variant_id !== variantId
      );
      console.log('DEBUG_CART: Removing item from cart', {variantId});
    } else {
      updatedCartItems = storeState.cart_items.map(item =>
        item.variant_id === variantId ? {...item, quantity: quantity} : item
      );
      console.log('DEBUG_CART: Updating item quantity', {productId, quantity});
    }

    const data = {
      ...storeState,
      cart_items: updatedCartItems,
    };

    console.log('DEBUG_CART: New cart state', JSON.stringify(data.cart_items));

    if (updatedCartItems.length === 0 && data.selected_screen === 'CART') {
      data.selected_screen = 'SELECT_PRODUCT';
    }

    setStoreState(data);

    // Generate a timestamp for this update
    const updateTimestamp = Date.now();
    latestUpdateTimestampRef.current = updateTimestamp;

    // Update cart on server with debounce and timestamp
    debouncedUpdateCartOnServer(
      updatedCartItems,
      storeState.discountConfig,
      updateTimestamp
    );
  };

  // Apply one quantity to several variants in a SINGLE state update. Looping
  // updateCartItems would make every call read the same stale storeState, so
  // only the last variant would survive.
  const updateCartItemsMulti = (entries, quantity) => {
    if (!entries || !entries.length) {
      return;
    }

    let updatedCartItems = [...storeState.cart_items];

    entries.forEach(({variantId, productId, productContext, collectionId}) => {
      const existing = updatedCartItems.find(
        item => item.variant_id === variantId
      );

      if (existing) {
        updatedCartItems =
          quantity === 0
            ? updatedCartItems.filter(item => item.variant_id !== variantId)
            : updatedCartItems.map(item =>
                item.variant_id === variantId
                  ? {
                      ...item,
                      quantity,
                      // Last section touched wins. A product can sit in several
                      // collections, and if the agent adjusts it from a different
                      // one, the badge has to move where they are working rather
                      // than tick up on a section that may be scrolled away.
                      collection_id: collectionId || item.collection_id || null,
                    }
                  : item
              );
        return;
      }

      if (quantity === 0) {
        return;
      }

      const product =
        filteredProducts.find(
          pr => String(pr.productId) === String(productId)
        ) ||
        products.find(pr => String(pr.productId) === String(productId)) ||
        getCartProductFromLocalStorage(productId);
      const variant = (product?.variants || []).find(
        v => String(v.id) === String(variantId)
      );

      if (!variant && !productContext) {
        console.error('DEBUG_CART: Variant not found for bulk add', {
          variantId,
          productId,
        });
        return;
      }

      updatedCartItems = [
        ...updatedCartItems,
        {
          variant_id: variantId,
          quantity,
          product_id: productId,
          title: product?.title || productContext?.productTitle,
          price: variant?.price || productContext?.price,
          variantTitle: variant?.title || productContext?.variantTitle,
          sku: variant?.sku || productContext?.sku,
          // Which section the agent added this from. Persisted with the cart, so
          // the section can show a cart badge without having to load products.
          collection_id: collectionId || null,
        },
      ];
    });

    const data = {...storeState, cart_items: updatedCartItems};
    if (updatedCartItems.length === 0 && data.selected_screen === 'CART') {
      data.selected_screen = 'SELECT_PRODUCT';
    }
    setStoreState(data);

    const updateTimestamp = Date.now();
    latestUpdateTimestampRef.current = updateTimestamp;
    debouncedUpdateCartOnServer(
      updatedCartItems,
      storeState.discountConfig,
      updateTimestamp
    );
  };

  const toggleCartScreen = () => {
    if (
      storeState.cart_items.length === 0 &&
      storeState.selected_screen === 'SELECT_PRODUCT'
    )
      return;
    const data = {
      ...storeState,
      selected_screen:
        storeState.selected_screen === 'CART' ? 'SELECT_PRODUCT' : 'CART',
    };
    setStoreState(data);

    // Save to localStorage
    saveCartToLocalStorage(data);
  };

  const updateCartVariant = (variantId, newVariantId) => {
    console.log(variantId, newVariantId, 'variantId, newVariantId');
    const updatedCartItems = storeState.cart_items.map(item =>
      item.variant_id === variantId
        ? {
            ...item,
            variant_id: newVariantId,
            title: item.title,
            price: item.price,
            variantTitle: item.variantTitle,
          }
        : item
    );

    const data = {
      ...storeState,
      cart_items: updatedCartItems,
    };
    setStoreState(data);

    // Generate a timestamp for this update
    const updateTimestamp = Date.now();
    latestUpdateTimestampRef.current = updateTimestamp;

    // Update cart on server with debounce and timestamp
    debouncedUpdateCartOnServer(
      updatedCartItems,
      storeState.discountConfig,
      updateTimestamp
    );
  };

  const updateAddress = (key, value) => {
    const data = {
      ...storeState,
      shipping_details: {...storeState.shipping_details, [key]: value},
    };
    setStoreState(data);

    // Save to localStorage
    saveCartToLocalStorage(data);
  };

  const updateAllAddressData = updatedData => {
    const data = {
      ...storeState,
      shipping_details: {...storeState.shipping_details, ...updatedData},
    };
    setStoreState(data);

    // Save to localStorage
    saveCartToLocalStorage(data);
  };

  const updateShippingCharges = value => {
    const data = {
      ...storeState,
      shipping_charges: value,
    };
    setStoreState(data);

    // Save to localStorage
    saveCartToLocalStorage(data);

    // Generate a timestamp for this update
    const updateTimestamp = Date.now();
    latestUpdateTimestampRef.current = updateTimestamp;

    // Update cart on server with debounce and timestamp
    debouncedUpdateCartOnServer(
      data.cart_items,
      storeState.discountConfig,
      updateTimestamp
    );
  };

  const updateTags = (value, index, isRemove) => {
    const data = {
      ...storeState,
      tags: isRemove
        ? storeState.tags.filter((_, i) => i !== index)
        : [...storeState.tags, value],
    };
    setStoreState(data);

    // Save to localStorage
    saveCartToLocalStorage(data);
  };

  const updateNotes = value => {
    const data = {
      ...storeState,
      notes: value,
    };
    setStoreState(data);

    // Save to localStorage
    saveCartToLocalStorage(data);
  };

  const updateAllowCod = value => {
    const data = {
      ...storeState,
      allow_cod: value,
    };
    setStoreState(data);

    // Save to localStorage
    saveCartToLocalStorage(data);
  };

  // Accepts a single variant id or several. Multiple variants are still sent one
  // request at a time, but reported with ONE toast instead of one per variant.
  const sendProductInChat = async variantIdOrIds => {
    const variantIds = (
      Array.isArray(variantIdOrIds) ? variantIdOrIds : [variantIdOrIds]
    ).filter(Boolean);
    if (!variantIds.length) {
      return;
    }
    const sentLabel =
      variantIds.length === 1 ? 'Product' : `${variantIds.length} products`;

    try {
      const formattedPhoneNumber = getEffectivePhoneNumber();
      const isInstagram = detailsFromChatwoot.instagramSourceId;
      const isWhatsApp = formattedPhoneNumber;

      if (!isInstagram && !isWhatsApp) {
        messageApi.error('No contact information found to send product');
        return;
      }

      setApiCallLoading(true);

      if (isInstagram) {
        // Instagram flow
        if (!detailsFromChatwoot.instagramPageId) {
          messageApi.error('Instagram page not configured');
          return;
        }

        for (const variantId of variantIds) {
          await axios.post(
            `${CONTAINER_BASE_URL_SHOPPING}/sendInstagramProductItem`,
            {
              shopUrl: shopDetailsConfig.shopUrl,
              instagramPageId: detailsFromChatwoot.instagramPageId,
              instagramSourceId:
                detailsFromChatwoot.instagramSourceId ||
                detailsFromChatwoot.igsid,
              variantId: variantId,
              chatWootConversationId: detailsFromChatwoot.conversationId,
            }
          );
        }
        messageApi.success(`${sentLabel} sent on Instagram!`);
      } else if (isWhatsApp) {
        // WhatsApp flow (existing logic)
        const {phoneNumber, error, corrected} = resolveMessagingPhoneNumber(
          formattedPhoneNumber,
          getChatwootPhoneNumber()
        );
        if (error) {
          messageApi.error(error);
          return;
        }
        if (corrected) {
          console.warn(
            `Shipping phone ${sanitizePhoneNumber(
              formattedPhoneNumber
            )} is not this conversation's contact; messaging ${phoneNumber} instead.`
          );
        }

        for (const variantId of variantIds) {
          await axios.post(`${CONTAINER_BASE_URL_SHOPPING}/sendProductItem`, {
            shopUrl: shopDetailsConfig.shopUrl,
            phoneNumber: phoneNumber,
            variantId: variantId,
            chatWootConversationId: detailsFromChatwoot.conversationId,
            shopPhoneNumber: shopDetailsConfig.shopPhoneNumber,
          });
        }
        messageApi.success(`${sentLabel} sent successfully`);
      }
    } catch (error) {
      messageApi.error(
        variantIds.length === 1
          ? 'Error sending product'
          : 'Error sending products'
      );
      console.log(error, 'error');
    } finally {
      setApiCallLoading(false);
    }
  };

  const createCodOrder = async () => {
    try {
      if (
        !storeState.shipping_details.address1 ||
        !storeState.shipping_details.city ||
        !storeState.shipping_details.country ||
        !storeState.shipping_details.zip ||
        !storeState.shipping_details.province
      ) {
        messageApi.error('Please fill all the address details');
        return;
      }
      // Ensure we have a valid phone number before proceeding
      const formattedPhoneNumber = getEffectivePhoneNumber();
      if (!formattedPhoneNumber) {
        messageApi.error('No phone number found for COD order');
        return;
      }
      const {isDiscountOverLimit: overLimit, maxDiscountPercentage: cap} =
        getDiscountLimit(
          storeState.cart_items,
          storeState.discountConfig,
          shopDetailsConfig?.maxDiscountPercentage
        );
      if (overLimit) {
        messageApi.error(
          `Discount cannot exceed ${cap}% of cart value for this store`
        );
        return;
      }
      setApiCallLoading(true);
      // Build payload directly from cart items to avoid dependency on products list
      const cartItemsFromProduct = storeState.cart_items.map(item => ({
        variant_id: item.variant_id,
        item_price: item.price,
        quantity: item.quantity,
        title: item.title,
      }));
      await axios.post(`${CONTAINER_BASE_URL_SHOPPING}/createCodOrder`, {
        shopUrl: shopDetailsConfig.shopUrl,
        phoneNumber: formattedPhoneNumber,
        productItems: cartItemsFromProduct,
        shippingAddress: {
          ...storeState.shipping_details,
          phone: getRawPhoneNumberForShipping(),
          first_name: storeState?.shipping_details?.first_name || shopDetailsConfig.firstName || detailsFromChatwoot.userFirstName || 'User',
          last_name: storeState?.shipping_details?.last_name || shopDetailsConfig.lastName || 'N/A',
        },
        discountConfig: {
          type: storeState.discountConfig.type,
          value: storeState.discountConfig.discountValue,
        },
        shippingCharges: storeState.shipping_charges,
        tags: storeState.tags,
        notes: storeState.notes,
        firstName: storeState?.shipping_details?.first_name || shopDetailsConfig.firstName || detailsFromChatwoot.userFirstName || 'User',
        chatWootConversationId: detailsFromChatwoot.conversationId,
        agentId: detailsFromChatwoot.agentId,
      });
    } catch (error) {
      // The backend rejects an over-limit discount with an agent-facing reason.
      messageApi.error(
        error?.response?.data?.error || 'Error creating COD order'
      );
      console.log(error, 'error');
    } finally {
      setApiCallLoading(false);
    }
  };

  const sendPaymentLink = async () => {
    try {
      if (
        !storeState.shipping_details.address1 ||
        !storeState.shipping_details.city ||
        !storeState.shipping_details.country ||
        !storeState.shipping_details.zip ||
        !storeState.shipping_details.province
      ) {
        messageApi.error('Please fill all the address details');
        return;
      }
      // Ensure we have a valid phone number before proceeding
      const formattedPhoneNumber = getEffectivePhoneNumber();
      if (!formattedPhoneNumber) {
        messageApi.error('No phone number found for payment link');
        return;
      }
      const {isDiscountOverLimit: overLimit, maxDiscountPercentage: cap} =
        getDiscountLimit(
          storeState.cart_items,
          storeState.discountConfig,
          shopDetailsConfig?.maxDiscountPercentage
        );
      if (overLimit) {
        messageApi.error(
          `Discount cannot exceed ${cap}% of cart value for this store`
        );
        return;
      }
      const cartItemsFromProduct = storeState.cart_items.map(item => ({
        variant_id: item.variant_id,
        item_price: item.price,
        quantity: item.quantity,
        title: item.title,
      }));
      setApiCallLoading(true);
      await axios.post(
        `${CONTAINER_BASE_URL_SHOPPING}/createPrepaidOrderLink`,
        {
          shopUrl: shopDetailsConfig.shopUrl,
          phoneNumber: formattedPhoneNumber,
          productItems: cartItemsFromProduct,
          shippingAddress: {
            ...storeState.shipping_details,
            phone: getRawPhoneNumberForShipping(),
            first_name:
              shopDetailsConfig.firstName || detailsFromChatwoot.userFirstName,
            last_name: shopDetailsConfig.lastName || 'N/A',
          },
          discountConfig: {
            type: storeState.discountConfig.type,
            amount: storeState.discountConfig.discountValue,
          },
          shippingCharges: storeState.shipping_charges,
          tags: storeState.tags,
          notes: storeState.notes,
          firstName:
            shopDetailsConfig.firstName || detailsFromChatwoot.userFirstName,
          chatWootConversationId: detailsFromChatwoot.conversationId,
          agentId: detailsFromChatwoot.agentId,
        }
      );
      messageApi.success('Payment link will be sent shortly');
    } catch (error) {
      messageApi.error(
        error?.response?.data?.error || 'Error sending payment link'
      );
      console.log(error, 'error');
    } finally {
      setApiCallLoading(false);
    }
  };

  const addUtmParamsToUrl = (url, utmParams) => {
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('utm_source', utmParams.utmSource);
      urlObj.searchParams.set('utm_medium', utmParams.utmMedium);
      urlObj.searchParams.set('utm_campaign', utmParams.utmCampaign);
      return urlObj.toString();
    } catch (error) {
      console.log(error, 'error');
      return url;
    }
  };

  const copyCheckoutLink = async () => {
    try {
      setApiCallLoading(true);
      const formattedPhoneNumber = getEffectivePhoneNumber();
      if (!formattedPhoneNumber) {
        messageApi.error(
          'Please add a phone number before generating a checkout link'
        );
        return;
      }

      const {isDiscountOverLimit: overLimit, maxDiscountPercentage: cap} =
        getDiscountLimit(
          storeState.cart_items,
          storeState.discountConfig,
          shopDetailsConfig?.maxDiscountPercentage
        );
      if (overLimit) {
        messageApi.error(
          `Discount cannot exceed ${cap}% of cart value for this store`
        );
        return;
      }
      const isCartSynced = await updateCartOnServer(
        storeState.cart_items,
        storeState.discountConfig,
        formattedPhoneNumber
      );
      if (!isCartSynced) {
        return;
      }

      const apiUrl = `${CONTAINER_BASE_URL_CHATWOOT}/draftOrder/${shopDetailsConfig.shopUrl}/${formattedPhoneNumber}`;

      // Make the API call to get the invoice URL
      const response = await axios.post(apiUrl, {
        discountConfig: {
          type: storeState.discountConfig.type,
          value: storeState.discountConfig.value,
        },
        shippingAddress: {
          ...storeState.shipping_details,
          phone: getRawPhoneNumberForShipping(),
          first_name: storeState.shipping_details.first_name || 'User',
          last_name: storeState.shipping_details.last_name || '',
        },
      });

      let invoiceUrl = response.data?.invoice_url;
      if (shopDetailsConfig?.shopUrl === 'pet-project-india.myshopify.com') {
        invoiceUrl = addUtmParamsToUrl(invoiceUrl, {
          utmSource: 'Bitespeed',
          utmMedium: 'WhatsApp_Store',
          utmCampaign: 'WhatsApp_Store',
        });
      }
      if (!invoiceUrl) {
        messageApi.error('No invoice URL found in response');
        return;
      }

      // Copy the invoice URL to clipboard
      await navigator.clipboard.writeText(invoiceUrl);
      messageApi.success('Invoice link copied to clipboard!');
    } catch (error) {
      messageApi.error(
        error?.response?.data?.message || 'Error generating invoice link'
      );
      console.log(error, 'error');
    } finally {
      setApiCallLoading(false);
    }
  };

  const fetchShippingCharges = async () => {
    try {
      setApiCallLoading(true);
      // Compute using values already stored on cart items to avoid product lookups
      const totalCartValue = storeState.cart_items.reduce(
        (acc, item) =>
          acc + (Number(item.price) || 0) * (Number(item.quantity) || 0),
        0
      );
      const response = await axios.get(
        `${CONTAINER_BASE_URL_SHOPPING}/getShippingCharges?shopUrl=${
          shopDetailsConfig.shopUrl
        }&grandTotal=${totalCartValue}&zip=${
          storeState.shipping_details.zip
        }&province=${storeState.shipping_details.province}&country=${
          storeState.shipping_details.country
        }&city=${storeState.shipping_details.city}&address1=${
          storeState.shipping_details.address1
        }&phone=${encodeURIComponent(getRawPhoneNumberForShipping() || '')}`
      );
      const shippingCharges = response.data?.shippingCharges;
      console.log(shippingCharges, 'shippingCharges');
      if (shippingCharges) {
        setStoreState(prev => ({
          ...prev,
          shipping_charges: shippingCharges.shippingRate,
        }));
      } else {
        messageApi.error('No shipping charges found for address.');
      }
    } catch (error) {
      messageApi.error('Error fetching shipping charges');
      console.log(error, 'error');
    } finally {
      setApiCallLoading(false);
    }
  };

  const sendCartMessage = async () => {
    setApiCallLoading(true);
    try {
      const cartItemsFromProduct = storeState.cart_items.map(item => ({
        variant_id: item.variant_id,
        item_price: item.price,
        quantity: item.quantity,
      }));
      // Ensure we have a valid phone number before proceeding
      const formattedPhoneNumber = getEffectivePhoneNumber();
      if (!formattedPhoneNumber) {
        messageApi.error('No phone number found to send cart message');
        return;
      }
      const payload = {
        shopUrl: shopDetailsConfig.shopUrl,
        phoneNumber: formattedPhoneNumber,
        productItems: cartItemsFromProduct,
        shippingAddress: {
          zip: storeState.shipping_details.zip,
          city: storeState.shipping_details.city,
          phone: getRawPhoneNumberForShipping(),
          country: storeState.shipping_details.country,
          address1: storeState.shipping_details.address1,
          province: storeState.shipping_details.province,
          first_name:
            shopDetailsConfig.firstName || detailsFromChatwoot.userFirstName,
          last_name: shopDetailsConfig.lastName || '',
        },
        notes: storeState.notes,
        tags: storeState.tags,
      };
      await axios.post(`${BEUrl}/sendCheckoutOnWhatsappMessage`, payload);
      messageApi.success('Checkout message sent successfully');
      setIsMessageSent(true);
    } catch (error) {
      messageApi.error('Error sending checkout message');
      console.log(error, 'error');
    } finally {
      setApiCallLoading(false);
    }
  };

  // Modify the updateDiscountConfig function
  const updateDiscountConfig = (type, value) => {
    // Calculate the new discount value based on type and cart total
    let newDiscountConfig = {
      ...storeState.discountConfig,
      [type]: value,
    };
    // Calculate total cart value using item.price directly (more reliable than product lookup)
    const totalCartValue = storeState.cart_items.reduce(
      (acc, item) =>
        acc + (Number(item.price) || 0) * (Number(item.quantity) || 0),
      0
    );
    console.log('DEBUG_DISCOUNT: updateDiscountConfig called', {
      type,
      value,
      cartItems: storeState.cart_items,
      totalCartValue,
      newDiscountConfig,
    });
    if (newDiscountConfig.type === 'percentage') {
      newDiscountConfig.value =
        (totalCartValue * parseFloat(newDiscountConfig.discountValue || 0)) /
        100;
    } else if (newDiscountConfig.type === 'flat') {
      newDiscountConfig.value = parseFloat(
        newDiscountConfig.discountValue || 0
      );
    } else {
      newDiscountConfig.value = newDiscountConfig.discountValue || 0;
    }
    const updatedState = {
      ...storeState,
      discountConfig: newDiscountConfig,
    };
    setStoreState(updatedState);
    saveCartToLocalStorage(updatedState);
    const updateTimestamp = Date.now();
    latestUpdateTimestampRef.current = updateTimestamp;
    debouncedUpdateCartOnServer(
      updatedState.cart_items,
      updatedState.discountConfig,
      updateTimestamp
    );
  };

  const updateAllowPrepaid = value => {
    const data = {
      ...storeState,
      allow_prepaid: value,
    };
    setStoreState(data);

    // Save to localStorage
    saveCartToLocalStorage(data);
  };

  const fetchProducts = async (shopUrl, searchValue, pageNumber = 0) => {
    let queryParams = `?pageSize=50&pageNumber=${pageNumber}`;
    if (searchValue) {
      queryParams += `&search=${searchValue}`;
    }
    try {
      const url = `${CONTAINER_BASE_URL_CHATWOOT}/getProducts/${shopUrl}${queryParams}`;
      console.log('[ui.app] GET products', {
        url,
        shopUrl,
        pageNumber,
        hasSearch: !!searchValue,
      });
      const response = await axios.get(url);
      const products = response.data.products;
      const productItems = products.map(product => {
        return {
          title: product.title,
          productId: product.id,
          variants: product.variants.map(variant => {
            return {
              id: variant.id,
              title: variant.title,
              price: variant.price,
              sku: variant.sku,
            };
          }),
          image: product.image?.src,
        };
      });
      console.log('[ui.app] products fetched', {count: productItems.length});
      return productItems;
    } catch (error) {
      messageApi.error('Error fetching products');
      console.error('[ui.app] error fetching products', error);
      return [];
    }
  };

  const loadMoreProducts = async () => {
    if (!hasMoreProducts || !shopDetailsConfig.shopUrl) return;

    const nextPage = currentPage + 1;
    try {
      // Fetch next page with current search value
      const newProducts = await fetchProducts(
        shopDetailsConfig.shopUrl,
        searchValue,
        nextPage
      );

      if (newProducts.length === 0) {
        setHasMoreProducts(false);
        return;
      }

      // Filter out invalid products before setting state
      const validNewProducts = newProducts.filter(isProductValid);

      // Show the validation stats for debugging
      const invalidProductsCount = newProducts.length - validNewProducts.length;
      if (invalidProductsCount > 0) {
        console.log(
          `Product validation: Filtered out ${invalidProductsCount} invalid products from load more`
        );
      }

      console.log(
        `Product validation: ${newProducts.length} products fetched, ${validNewProducts.length} valid products`
      );

      // If we're searching, add to filtered products
      if (searchValue) {
        setFilteredProducts(prev => [...prev, ...validNewProducts]);
      } else {
        // If not searching, add to both main products and filtered view
        setProducts(prev => [...prev, ...validNewProducts]);
        setFilteredProducts(prev => [...prev, ...validNewProducts]);
      }

      setCurrentPage(nextPage);
    } catch (error) {
      console.error('Error loading more products:', error);
      messageApi.error('Error loading more products');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchProducts = useCallback(
    debounce((shopUrl, searchValue) => {
      const fetchInitialProducts = async () => {
        // Only set search loading if we're actually searching
        if (searchValue) {
          setIsSearchLoading(true);
        }

        // If there's a search value, we'll fetch and filter
        if (searchValue) {
          // Reset pagination for the search
          setCurrentPage(0);

          // Fetch the first page of search results
          const searchResults = await fetchProducts(shopUrl, searchValue);

          // Filter out invalid products before setting state
          const validSearchResults = searchResults.filter(isProductValid);
          console.log(
            `Product validation: ${searchResults.length} search results fetched, ${validSearchResults.length} valid products`
          );

          // Create filtered view without replacing the main products state
          setFilteredProducts(validSearchResults);

          // Set flag for more products if we got a full page (consider that some might be filtered)
          setHasMoreProducts(searchResults.length === 50);
        } else {
          // If no search value, fetch normal products if we don't have any
          if (products.length === 0) {
            const initialProducts = await fetchProducts(shopUrl, '');
            const validInitialProducts = initialProducts.filter(isProductValid);
            console.log(
              `Product validation: ${initialProducts.length} initial products fetched, ${validInitialProducts.length} valid products`
            );

            setProducts(validInitialProducts);
            setFilteredProducts(validInitialProducts);
            setHasMoreProducts(validInitialProducts.length === 50);
          } else {
            // If we already have products, just show all of them (filter in case products array contains invalid items)
            const validExistingProducts = products.filter(isProductValid);
            setFilteredProducts(validExistingProducts);
          }
        }

        setIsSearchLoading(false);
      };

      fetchInitialProducts();
    }, 700),
    [] // Remove products dependency to prevent duplicate searches
  );

  const fetchStoreConfig = async shopDetailsConfig => {
    try {
      const accountId = shopDetailsConfig.accountId || getAccountId();

      const inboxId = shopDetailsConfig.inboxId || getInboxId();

      if (!accountId || !inboxId) {
        throw new Error(
          'Missing account id and inbox id for fetching store config'
        );
      }
      const sanitizedPhone = sanitizePhoneNumber(shopDetailsConfig.phoneNumber);
      const queryParams = new URLSearchParams({accountId});
      queryParams.append('inboxId', inboxId);
      if (sanitizedPhone) {
        queryParams.append('phoneNumber', sanitizedPhone);
      }
      const response = await axios.get(
        `${CONTAINER_BASE_URL_SHOPPING}/getSupportShoppingConfig?${queryParams.toString()}`
      );
      const storeConfig = response?.data?.data || {};

      // Preserve the igsid from the original shopDetailsConfig
      const updatedConfig = {
        ...storeConfig,
        igsid: shopDetailsConfig.igsid, // Preserve the igsid
        instagramPageId: shopDetailsConfig.instagramPageId,
        instagramSourceId: shopDetailsConfig.instagramSourceId,
        phoneNumber: sanitizedPhone || storeConfig.phoneNumber,
        accountId,
      };

      setShopDetailsConfig(updatedConfig);

      setStoreState(prev => ({
        ...prev,
        shipping_details: {
          first_name:
            prev.shipping_details.first_name ||
            storeConfig?.shippingDetails?.first_name ||
            shopDetailsConfig.firstName ||
            detailsFromChatwoot.userFirstName ||
            '',
          last_name:
            prev.shipping_details.last_name ||
            storeConfig?.shippingDetails?.last_name ||
            detailsFromChatwoot?.userLastName ||
            '',
          zip: prev.shipping_details.zip || storeConfig.shippingDetails?.zip,
          city: prev.shipping_details.city || storeConfig.shippingDetails?.city,
          phone:
            prev.shipping_details.phone ||
            storeConfig.shippingDetails?.phone ||
            shopDetailsConfig.phoneNumber ||
            '',
          country:
            prev.shipping_details.country ||
            storeConfig.shippingDetails?.country,
          address1:
            prev.shipping_details.address1 ||
            storeConfig.shippingDetails?.address1,
          province:
            prev.shipping_details.province ||
            storeConfig.shippingDetails?.province,
        },
      }));

      // Set isLoading to false after successfully fetching store config
      setIsLoading(false);
    } catch (error) {
      messageApi.error('Error fetching store details');
      console.log(error, 'error');

      // Set isLoading to false even on error
      setIsLoading(false);
    }
  };

  const handlePhoneNumberUpdate = async rawPhoneNumber => {
    const sanitizedPhone = sanitizePhoneNumber(rawPhoneNumber);
    if (!isValidPhoneNumber(sanitizedPhone)) {
      messageApi.error('Please enter a valid phone number');
      return;
    }

    const previousPhone = sanitizePhoneNumber(shopDetailsConfig.phoneNumber);
    if (previousPhone === sanitizedPhone) {
      return;
    }

    try {
      setApiCallLoading(true);
      const updatedShippingDetails = {
        ...storeState.shipping_details,
        phone: sanitizedPhone,
      };
      setStoreState(prev => ({
        ...prev,
        shipping_details: updatedShippingDetails,
      }));

      try {
        await updateContactPhoneNumberInChatwoot(sanitizedPhone);
      } catch (error) {
        console.error('Error updating Chatwoot contact', error);
      }

      const configWithPhone = {
        ...shopDetailsConfig,
        phoneNumber: sanitizedPhone,
      };
      setShopDetailsConfig(configWithPhone);
      await fetchStoreConfig(configWithPhone);
      if (storeState.cart_items.length > 0) {
        await updateCartOnServer(
          storeState.cart_items,
          storeState.discountConfig,
          sanitizedPhone
        );
      }
      messageApi.success('Phone number updated');
    } catch (error) {
      console.error('Error updating phone details', error);
      messageApi.error('Error updating phone number');
    } finally {
      setApiCallLoading(false);
    }
  };

  const clearCartOnServer = async () => {
    try {
      const formattedPhoneNumber = getEffectivePhoneNumber();
      if (!shopDetailsConfig.shopUrl || !formattedPhoneNumber) return;

      // Make API call to clear the cart
      await axios.post(
        `${CONTAINER_BASE_URL_CHATWOOT}/modifyCart/${shopDetailsConfig.shopUrl}/${formattedPhoneNumber}`,
        {
          cartData: {
            cartItems: [],
            discount: null,
            shippingCharges: 0,
          },
        }
      );

      // Save empty cart to localStorage
      saveCartToLocalStorage({
        ...storeState,
        cart_items: [],
      });

      console.log('Cart cleared successfully on server');
    } catch (error) {
      console.error('Error clearing cart on server:', error);
      messageApi.error('Error clearing cart on server');

      // Even if server clear fails, clear localStorage
      saveCartToLocalStorage({
        ...storeState,
        cart_items: [],
      });
    }
  };

  const clearCart = () => {
    const emptyCart = [];
    setStoreState(prev => ({
      ...prev,
      cart_items: emptyCart,
    }));

    // Clear cart on server
    clearCartOnServer();
  };

  useEffect(() => {
    if (shopDetailsConfig.shopUrl) {
      debouncedFetchProducts(shopDetailsConfig.shopUrl, searchValue);
      checkTemplateAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopDetailsConfig.shopUrl, searchValue, debouncedFetchProducts]);

  useEffect(() => {
    if (shopDetailsConfig?.phoneNumber && shopDetailsConfig?.accountId) {
      console.log('DEBUG_CART: useEffect triggered to fetch cart data', {
        phoneNumber: shopDetailsConfig.phoneNumber,
        accountId: shopDetailsConfig.accountId,
        shopUrl: shopDetailsConfig.shopUrl,
        inboxId: shopDetailsConfig.inboxId,
        instagramPageId: shopDetailsConfig.instagramPageId,
        instagramSourceId: shopDetailsConfig.instagramSourceId,
        igsid: shopDetailsConfig.igsid,
      });

      const fetchAndSetCartData = async () => {
        const cartData = await fetchCartData(
          shopDetailsConfig.shopUrl,
          shopDetailsConfig.phoneNumber
        );
        if (cartData) {
          console.log(
            'DEBUG_CART: Setting state with fetched cart data',
            JSON.stringify(cartData.cart_items)
          );
          setStoreState(cartData);
        } else {
          console.log('DEBUG_CART: No cart data returned from fetch');
        }
      };

      fetchAndSetCartData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shopDetailsConfig?.phoneNumber,
    shopDetailsConfig?.accountId,
    shopDetailsConfig?.shopUrl,
  ]);

  // const randomFunction = async () => {
  // replace data here.

  //   const shopDetailsConfig = {
  //     phoneNumber: data?.conversation?.meta?.sender?.phone_number,
  //     accountId: data?.conversation?.account_id,
  //     conversationId: data?.conversation?.id,
  //     userFirstName: data?.contact?.name.split(' ')[0],
  //     agentId: data?.currentAgent?.id || null,
  //   };

  //   setDetailsFromChatwoot(shopDetailsConfig);

  //     if (shopDetailsConfig?.accountId && shopDetailsConfig?.phoneNumber) {
  //       await fetchStoreConfig(shopDetailsConfig);
  //     }
  // }

  // useEffect(() => {
  //   randomFunction();
  // }, []);

  useEffect(() => {
    // Generate a unique identifier for this iframe instance
    // const iframeId = Math.random().toString(36).substr(2, 9);
    let hasProcessedMessage = false;

    const ALLOWED_PARENTS = [
      'https://chat.bitespeed.co',
      'https://csdbtest.bitespeed.co',
      // 'http://localhost:3000'
    ];

    const handleMessage = async event => {
      // In the Chatwoot mobile app the context is injected via
      // window.postMessage from a react-native-webview, so event.origin is the
      // page's own origin (or "") rather than an allowed parent. Trust it when
      // we're running inside that WebView.
      const inWebView = !!window.ReactNativeWebView;
      // Browser-level origin allowlist — drop messages from any other parent.
      if (!inWebView && !ALLOWED_PARENTS.includes(event.origin)) return;

      let data = null;
      try {
        data = JSON.parse(event.data);
      } catch (error) {
        return;
      }

      if (!data?.event || data.event !== 'appContext') {
        return;
      }
      // Refuse payloads that did not come with a Chatwoot-minted token. The
      // mobile app's injected appContext has no bspdToken; fall back to the
      // URL token (appended by the Chatwoot server to the frame URL).
      const urlToken = new URLSearchParams(window.location.search).get(
        'bspd_token'
      );
      const token = data?.data?.bspdToken || urlToken;
      if (!token) return;

      // Always refresh the stored token where the axiosInstance interceptor
      // reads it. This must run on EVERY appContext (not just the first) so the
      // 401 → refresh-token handshake can land a fresh token on later messages.
      localStorage.setItem('token', token);

      // Everything below is one-time setup (store config + contact details).
      if (hasProcessedMessage) return;
      hasProcessedMessage = true;

      // Extract the latest incoming message timestamp
      if (
        data?.data?.conversation?.messages &&
        Array.isArray(data.data.conversation.messages)
      ) {
        const messages = data.data.conversation.messages;
        const incomingMessages = messages.filter(msg => msg.message_type === 0);

        if (incomingMessages.length > 0) {
          // Get the most recent incoming message (last in the array)
          const latestIncomingMessage =
            incomingMessages[incomingMessages.length - 1];

          // Store the latest incoming message timestamp
          setLatestIncomingMessageTimestamp(latestIncomingMessage.created_at);
        } else {
          setLatestIncomingMessageTimestamp(null);
        }
      }

      // Extract phone number and igsid
      const phoneNumber =
        data?.data?.conversation?.meta?.sender?.phone_number ||
        data?.data?.contact?.phone_number ||
        data?.data?.conversation?.contact_inbox?.source_id ||
        data?.data?.contact?.source_id;

      const igsid = data?.data?.conversation?.meta?.sender?.id;
      const instagramSourceId = data?.data?.instagramSourceId;
      const instagramPageId = data?.data?.instagramPageId;

      const shopDetailsConfig = {
        phoneNumber: phoneNumber,
        igsid: igsid,
        instagramSourceId: instagramSourceId,
        instagramPageId: instagramPageId,
        accountId: data?.data?.conversation?.account_id,
        inboxId: data?.data?.conversation?.inbox_id,
        conversationId: data?.data?.conversation?.id,
        userFirstName: data?.data?.contact?.name?.split(' ')[0] || '',
        userLastName: data?.data?.contact?.name?.split(' ')[1] || '',
        agentId: data?.data?.currentAgent?.id || null,
        contactId: data?.data?.contact?.id,
      };

      setDetailsFromChatwoot(shopDetailsConfig);

      if (shopDetailsConfig?.accountId) {
        await fetchStoreConfig(shopDetailsConfig);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Seed the URL token early so the axios interceptor has it before the
      // first appContext lands (mobile WebView has no postMessage handshake).
      const bootUrlToken = new URLSearchParams(window.location.search).get(
        'bspd_token'
      );
      if (bootUrlToken) localStorage.setItem('token', bootUrlToken);
      // In the mobile app WebView window.parent is the page itself, so route
      // the handshake through the RN bridge; fall back to the iframe parent.
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage('chatwoot-dashboard-app:fetch-info');
      } else {
        window.parent.postMessage('chatwoot-dashboard-app:fetch-info', '*');
      }

      // Add a timeout to set default values if no data is received
      // setTimeout(() => {
      //   // If no data received, set default values for testing
      //   if (!shopDetailsConfig.accountId) {
      //     const defaultConfig = {
      //       phoneNumber: null,
      //       igsid: '16531369', // Use the igsid you provided
      //       accountId: 643,
      //       conversationId: null,
      //       userFirstName: 'User',
      //       agentId: null,
      //       shopUrl: 'nitishkastore.myshopify.com',
      //       contactId: null,
      //     };
      //     setDetailsFromChatwoot(defaultConfig);
      //   }
      // }, 10000);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window]);

  if (isLoading) {
    return <Loader />;
  }

  let screen;

  // Determine currency symbol from store configuration (support many currencies)
  const CURRENCY_SYMBOLS = {
    AED: 'د.إ',
    ALL: 'L',
    ANG: 'ƒ',
    AOA: 'Kz',
    ARS: '$',
    AUD: '$',
    BAM: 'KM',
    BDT: '৳',
    BHD: 'د.ب',
    BOB: 'Bs.',
    BRL: 'R$',
    CAD: '$',
    CHF: 'CHF',
    CLP: '$',
    CNY: '¥',
    COP: '$',
    CRC: '₡',
    CZK: 'Kč',
    DOP: 'RD$',
    DZD: 'دج',
    EGP: 'E£',
    EUR: '€',
    GBP: '£',
    GHS: '₵',
    GNF: 'FG',
    GTQ: 'Q',
    HKD: '$',
    HNL: 'L',
    HUF: 'Ft',
    IDR: 'Rp',
    ILS: '₪',
    INR: '₹',
    IQD: 'ع.د',
    JOD: 'د.ا',
    JPY: '¥',
    KES: 'KSh',
    KHR: '៛',
    KRW: '₩',
    KWD: 'د.ك',
    KYD: '$',
    LBP: 'ل.ل',
    LKR: 'Rs',
    LYD: 'ل.د',
    MAD: 'د.م.',
    MRU: 'UM',
    MXN: '$',
    MYR: 'RM',
    NGN: '₦',
    NOK: 'kr',
    NPR: 'रू',
    NZD: '$',
    OMR: 'ر.ع.',
    PAB: 'B/.',
    PEN: 'S/',
    PHP: '₱',
    PKR: 'Rs',
    PLN: 'zł',
    PYG: '₲',
    QAR: 'ر.ق',
    RON: 'lei',
    RUB: '₽',
    RWF: 'FRw',
    SAR: 'ر.س',
    SEK: 'kr',
    SGD: '$',
    THB: '฿',
    TND: 'د.ت',
    TRY: '₺',
    TTD: 'TT$',
    TZS: 'TSh',
    UGX: 'USh',
    USD: '$',
    UYU: '$U',
    VND: '₫',
    XAF: 'FCFA',
    XOF: 'CFA',
    XPF: '₣',
    ZAR: 'R',
  };
  const currencyCode = (shopDetailsConfig?.currency || 'INR').toUpperCase();
  const currencySymbol = CURRENCY_SYMBOLS[currencyCode] || currencyCode;
  const activePhoneNumber = getEffectivePhoneNumber();

  // Derived on every render rather than stored, so a cart restored from
  // localStorage or the cart server is checked with the same rule as a freshly
  // typed discount.
  const {maxDiscountPercentage, maxDiscountAmount, isDiscountOverLimit} =
    getDiscountLimit(
      storeState.cart_items,
      storeState.discountConfig,
      shopDetailsConfig?.maxDiscountPercentage
    );

  if (storeState.selected_screen === 'CART') {
    screen = (
      <CartSection
        updateDiscountConfig={updateDiscountConfig}
        sendCartMessage={sendCartMessage}
        toggleCartScreen={toggleCartScreen}
        updateAddress={updateAddress}
        updateAllAddressData={updateAllAddressData}
        onPhoneNumberUpdate={handlePhoneNumberUpdate}
        updateCartItems={updateCartItems}
        storeState={storeState}
        productItems={products}
        shopUrl={shopDetailsConfig.shopUrl}
        updateShippingCharges={updateShippingCharges}
        updateTags={updateTags}
        updateNotes={updateNotes}
        updateAllowCod={updateAllowCod}
        updateAllowPrepaid={updateAllowPrepaid}
        isMessageSent={isMessageSent}
        fetchShippingCharges={fetchShippingCharges}
        createCodOrder={createCodOrder}
        sendPaymentLink={sendPaymentLink}
        copyCheckoutLink={copyCheckoutLink}
        isSendInChatDisabled={getSendInChatDisabled()}
        isWhatsappCheckoutEnabled={shopDetailsConfig.checkoutONWhatsappEnabled}
        shopDetailsConfig={shopDetailsConfig}
        currencySymbol={currencySymbol}
        maxDiscountPercentage={maxDiscountPercentage}
        maxDiscountAmount={maxDiscountAmount}
        isDiscountOverLimit={isDiscountOverLimit}
      />
    );
  } else if (storeState.selected_screen === 'SELECT_PRODUCT') {
    screen = (
      <AddCartScreen
        sendProductInChat={sendProductInChat}
        toggleCartScreen={toggleCartScreen}
        cartItems={storeState.cart_items}
        products={filteredProducts}
        clearCart={clearCart}
        searchValue={searchValue}
        handleSearchChange={handleSearchChange}
        updateCartItems={updateCartItems}
        updateCartItemsMulti={updateCartItemsMulti}
        updateCartVariant={updateCartVariant}
        loadMoreProducts={loadMoreProducts}
        isSendInChatDisabled={getSendInChatDisabled()}
        isSearchLoading={isSearchLoading}
        shopUrl={shopDetailsConfig.shopUrl}
        shopPhoneNumber={shopDetailsConfig.shopPhoneNumber}
        shopDetailsConfig={shopDetailsConfig}
        detailsFromChatwoot={detailsFromChatwoot}
        currencySymbol={currencySymbol}
        hasMoreProducts={hasMoreProducts}
        activePhoneNumber={activePhoneNumber}
        selectedByCollection={selectedByCollection}
        setSelectedByCollection={setSelectedByCollection}
        selectedAllProducts={selectedAllProducts}
        setSelectedAllProducts={setSelectedAllProducts}
        activePanelKey={activePanelKey}
        setActivePanelKey={setActivePanelKey}
        showAllProducts={showAllProducts}
        setShowAllProducts={setShowAllProducts}
      />
    );
  }
  return (
    <div style={{height: '100vh', overflow: 'auto'}}>
      {contextHolder}
      <>{screen}</>
      {apiCallLoading && <Loader />}
    </div>
  );
}

export default App;
